import { Avatar, Div, Flex, Text } from "@hopper-ui/components";
import type { ReactNode } from "react";

interface Props {
    fullName: string;
    imageUrl?: string | null;
    jobTitle?: string | null;
    // Lines under the job title, such as the email the assistant's member card adds.
    children?: ReactNode;
    // Facts about the member, under the whole header rather than beside the avatar, so each gets the card's full width.
    details?: ReactNode;
}

// Who a member is, as their cards show it: a large avatar, the name, and the job title when they have one.
export const MemberDetails = ({ fullName, imageUrl, jobTitle, children, details }: Props) => {
    const header = (
        <Flex gap="inline-md" alignItems={details ? "center" : undefined}>
            <Div>
                <Avatar
                    data-private
                    src={imageUrl ?? undefined}
                    name={fullName}
                    size="lg"
                />
            </Div>
            {/* A flex item won't shrink below its widest unbreakable line unless told to: without this, a fact that ends in
                an ellipsis (a long manager's name) widens the column past the card instead of truncating. */}
            <Flex direction="column" gap="inline-sm" width="100%" minWidth="core_0">
                {/* The job title sits right under the name, as one line of who they are. */}
                <Flex direction="column" minWidth="core_0">
                    <Text fontSize="body-sm" fontWeight="heading-2xl" color="neutral">{fullName}</Text>
                    {jobTitle && <Text fontSize="body-xs" color="neutral-weak">{jobTitle}</Text>}
                </Flex>
                {children}
            </Flex>
        </Flex>
    );

    return details
        ? (
            <Flex direction="column" gap="stack-sm" minWidth="core_0">
                {header}
                {details}
            </Flex>
        )
        : header;
};
